import React, { useEffect, useState, useRef } from 'react';
import {
  Grid,
  TextField,
  Paper,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Button,
  Box,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Info as InfoIcon,
  FileCopy as DuplicateIcon
} from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import MainContainer from "../../../components/MainContainer";
import { headerDataMission } from "../../../data/modules/mission-header";
import Message from "../../tools/Message";
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import LocalisationMisService from '../../../services/LocalisationMisService';
import MissionService from '../../../services/MissionService';
import { LoadingButton } from '@mui/lab';
import { useParams } from "react-router-dom";
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import { Alert, CircularProgress } from '@mui/material';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    boxShadow: theme.shadows[3],
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  labelPosition: {
    fontWeight: 'bold',
    marginTop: theme.spacing(1),
    display: 'block',
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    boxShadow: theme.shadows[2],
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 'bold',
  },
  actionCell: {
    whiteSpace: 'nowrap',
    minWidth: '120px',
  },
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    padding: theme.spacing(0, 2),
  },
  infoText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  duplicateDateRow: {
    backgroundColor: '#fff3cd',
    '&:hover': {
      backgroundColor: '#ffe69c !important',
    },
  },
  duplicateRowHighlight: {
    borderLeft: '4px solid #ff9800',
  },
  activityCell: {
    minWidth: '200px',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  duplicateAlert: {
    color: '#e65100',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  activityButton: {
    textTransform: 'none',
  },
  dialogContent: {
    minWidth: '500px',
    position: 'relative',
  },
  formRow: {
    marginBottom: theme.spacing(2),
  },
  dialogLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    zIndex: 10,
    gap: theme.spacing(2),
  },
  loadingText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  fieldLoadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '56px',
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    gap: theme.spacing(1),
  },
  fieldLoadingText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  sectionTitle: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(1),
  },
  clickableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  financementFilterInfo: {
    display: 'block',
    fontSize: '0.7rem',
    color: theme.palette.info.main,
    marginTop: theme.spacing(0.5),
    fontStyle: 'italic',
  },
  autoGeneratedHint: {
    display: 'block',
    fontSize: '0.7rem',
    color: theme.palette.success.main,
    marginTop: theme.spacing(0.5),
    fontStyle: 'italic',
  },
}));

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
  },
}));

const ACTIVITY_TYPES = [
  { value: 'DR', label: 'Délais de route' },
  { value: 'hors_mis', label: 'Hors MIS' },
  { value: 'mis', label: 'MIS' }
];

const generateDefaultTitle = () => {
  const currentYear = new Date().getFullYear();
  const randomNumber = Math.floor(Math.random() * 99999) + 1;
  const paddedNumber = String(randomNumber).padStart(5, '0');
  return `AGD-${currentYear}-${paddedNumber}`;
};

const getPlaceholderTitle = () => {
  const currentYear = new Date().getFullYear();
  return `AGD-${currentYear}-00001`;
};

const getRowSignature = (row) => {
  if (!row) return '';
  const communeId = row.activityDetails?.commune?.id_commune ?? '';
  const communeArriveeId = row.activityDetails?.commune_arriver?.id_commune ?? '';
  const activiteMisIds = (row.activityDetails?.activite_mis || [])
    .map((a) => a.id_type_activite ?? a.id_activite_mis ?? a.id ?? '')
    .sort()
    .join(',');
  const financementIds = (row.activityDetails?.financement || [])
    .map((f) => f.id_financement ?? '')
    .sort()
    .join(',');

  return [
    row.dateDepart || '',
    row.dateArrivee || '',
    row.heureDepart || '',
    row.heureArrivee || '',
    row.activityType || '',
    communeId,
    communeArriveeId,
    activiteMisIds,
    financementIds,
    row.activityDetails?.activite_hors_mis || '',
  ].join('|');
};

const findDuplicateRowIds = (ranges) => {
  const signatureMap = new Map();
  const duplicateIds = new Set();

  ranges.forEach((row) => {
    const sig = getRowSignature(row);
    const hasContent = row.activityType || row.activityDetails?.commune;
    if (!hasContent) return;

    if (signatureMap.has(sig)) {
      duplicateIds.add(signatureMap.get(sig));
      duplicateIds.add(row.id);
    } else {
      signatureMap.set(sig, row.id);
    }
  });

  return duplicateIds;
};

// ============================================================
// FONCTION UTILITAIRE : identifiant unique pour une activité MIS
// ============================================================
const getActiviteMisKey = (a) => {
  if (!a) return '';
  return String(
    a.id_type_activite ??
    a.id_activite_mis ??
    a.id ??
    a.libelle_type_activite ??
    a.libelle_fr ??
    ''
  );
};

// ============================================================
// FONCTION UTILITAIRE : sécurise la valeur d'activite_mis
// (garantit que c'est toujours un tableau d'objets)
// ============================================================
const normalizeActiviteMis = (val) => {
  if (!val) return [];
  if (!Array.isArray(val)) return [];
  return val.filter(Boolean);
};

function AgendaMission(props) {
  const navigate = useNavigate();
  const classes = useStyles();
  const [isloading, setLoading] = useState(false);
  const [message, setMessage] = React.useState({
    open: false,
    color: "green",
    messageInfo: "",
  });
  const { open, color, messageInfo } = message;
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [currentActivityRowId, setCurrentActivityRowId] = React.useState(null);
  const params = useParams();
  const idagenda = params.modif;
  const iddestination = params.plid;

  const isMountedRef = useRef(true);

  const [dialogLoading, setDialogLoading] = React.useState(false);
  const [loadingDistrict, setLoadingDistrict] = React.useState(false);
  const [loadingDistrictArrive, setLoadingDistrictArrive] = React.useState(false);
  const [loadingCommune, setLoadingCommune] = React.useState(false);
  const [loadingCommuneArrive, setLoadingCommuneArrive] = React.useState(false);
  const [loadingActiviteMis, setLoadingActiviteMis] = React.useState(false);

  const [region, setRegion] = React.useState([]);
  const [district, setDistrict] = React.useState([]);
  const [commune, setCommune] = React.useState([]);
  const [districtarrive, setDistrictarrive] = React.useState([]);
  const [communearriver, setCommunearriver] = React.useState([]);
  const [financement, setFinancement] = React.useState([]);
  const [activitemis, setActivitemis] = React.useState([]);
  const [playdate, setPlaydate] = useState(true);
  const [intutule, setIntutule] = React.useState(() => generateDefaultTitle());
  const [planningid, setPlanningid] = React.useState(0);
  const [dateDebut, setDateDebut] = React.useState("");
  const [dateFin, setDateFin] = React.useState("");
  const [dateRanges, setDateRanges] = React.useState([]);
  const [isAddingNew, setIsAddingNew] = React.useState(false);
  const [typeactivite, setTypeactivite] = React.useState([]);
  const [actvalide, setActvalide] = React.useState(0);
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [listeplanning, setListeplanning] = useState([]);
  const [listedestination, setListeDestination] = useState([]);
  const [destinationid, setDestinationid] = useState(0);

  const [initialDataLoading, setInitialDataLoading] = React.useState(true);
  const [editDataLoading, setEditDataLoading] = React.useState(false);

  const canGoToPlanning = !!planningid && planningid !== 0;

  const [activityForm, setActivityForm] = React.useState({
    type: '',
    description: '',
    dateDepart: '',
    dateArrivee: '',
    heureDepart: '08:00',
    heureArrivee: '18:00',
    region: null,
    region_arriver: null,
    district: null,
    district_arriver: null,
    commune: null,
    commune_arriver: null,
    financement: [],
    activite_mis: [],
    activite_hors_mis: '',
  });

  const isTypeWithArrival = activityForm.type === 'DR';

  const financementOptions = React.useMemo(() => {
    if (!financement || financement.length === 0) return [];

    if (activityForm.type === 'mis') {
      return financement.filter(
        (f) => f.state && f.state.toLowerCase() === 'en cours'
      );
    }

    return financement;
  }, [financement, activityForm.type]);

  const duplicateRowIds = React.useMemo(
    () => findDuplicateRowIds(dateRanges),
    [dateRanges]
  );

  const hasDuplicates = duplicateRowIds.size > 0;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ============================================================
  // Chargement centralisé des activités MIS
  // ============================================================
  const loadActivitesMis = (financementArray, communeObj) => {
    const financementIds = (financementArray || [])
      .map(f => parseInt(f.id_financement))
      .filter(id => !isNaN(id));

    setLoadingActiviteMis(true);

    return LocalisationMisService.getactivitetype(
      financementIds,
      communeObj?.id_commune
    )
      .then((result) => {
        if (!isMountedRef.current) return;
        setActivitemis(result || []);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des activités MIS:", error);
        if (isMountedRef.current) setActivitemis([]);
      })
      .finally(() => {
        if (isMountedRef.current) setLoadingActiviteMis(false);
      });
  };

  // ============================================================
  // Ouverture du dialogue avec chargement complet des données
  // ============================================================
  const handleOpenActivityDialog = async (rowId) => {
    const row = dateRanges.find(r => r.id === rowId);
    if (!row) return;

    setCurrentActivityRowId(rowId);
    setDialogOpen(true);
    setDialogLoading(true);

    setDistrict([]);
    setCommune([]);
    setDistrictarrive([]);
    setCommunearriver([]);
    setActivitemis([]);
    setActvalide(null);

    const rowType = row.activityType || '';
    const isRowTypeWithArrival = rowType === 'DR';

    const needsDistrictDepart = !!row.activityDetails?.region?.id_region;
    const needsDistrictArrivee = isRowTypeWithArrival && !!row.activityDetails?.region_arriver?.id_region;
    const needsCommuneDepart = !!row.activityDetails?.district?.id_district;
    const needsCommuneArrivee = isRowTypeWithArrival && !!row.activityDetails?.district_arriver?.id_district;
    const needsActiviteMis = rowType === 'mis';

    setLoadingDistrict(needsDistrictDepart);
    setLoadingDistrictArrive(needsDistrictArrivee);
    setLoadingCommune(needsCommuneDepart);
    setLoadingCommuneArrive(needsCommuneArrivee);
    setLoadingActiviteMis(needsActiviteMis);

    try {
      let districtDepartPromise = Promise.resolve([]);
      if (needsDistrictDepart) {
        districtDepartPromise = LocalisationMisService
          .getDistrictByRegion(row.activityDetails.region.id_region)
          .then((result) => result?.data || [])
          .catch((error) => {
            console.error("Erreur lors de la récupération des districts (départ):", error);
            return [];
          });
      }

      let districtArriveePromise = Promise.resolve([]);
      if (needsDistrictArrivee) {
        districtArriveePromise = LocalisationMisService
          .getDistrictByRegion(row.activityDetails.region_arriver.id_region)
          .then((result) => result?.data || [])
          .catch((error) => {
            console.error("Erreur lors de la récupération des districts (arrivée):", error);
            return [];
          });
      }

      let communeDepartPromise = Promise.resolve([]);
      if (needsCommuneDepart) {
        communeDepartPromise = LocalisationMisService
          .getCommunesByDistrict(row.activityDetails.district.id_district)
          .then((result) => result?.data || [])
          .catch((error) => {
            console.error("Erreur lors de la récupération des communes (départ):", error);
            return [];
          });
      }

      let communeArriveePromise = Promise.resolve([]);
      if (needsCommuneArrivee) {
        communeArriveePromise = LocalisationMisService
          .getCommunesByDistrict(row.activityDetails.district_arriver.id_district)
          .then((result) => result?.data || [])
          .catch((error) => {
            console.error("Erreur lors de la récupération des communes (arrivée):", error);
            return [];
          });
      }

      let activiteMisPromise = Promise.resolve([]);
      if (needsActiviteMis) {
        const financementIds = (row.activityDetails?.financement || [])
          .map(f => parseInt(f.id_financement))
          .filter(id => !isNaN(id));

        const communeId = row.activityDetails?.commune?.id_commune;

        activiteMisPromise = LocalisationMisService
          .getactivitetype(financementIds, communeId)
          .then((result) => result || [])
          .catch((error) => {
            console.error("Erreur lors du chargement des activités MIS:", error);
            return [];
          });
      }

      const [
        districtsData,
        districtsArriveeData,
        communesData,
        communesArriveeData,
        activitesMisData
      ] = await Promise.all([
        districtDepartPromise,
        districtArriveePromise,
        communeDepartPromise,
        communeArriveePromise,
        activiteMisPromise
      ]);

      if (!isMountedRef.current) return;

      setDistrict(districtsData);
      setDistrictarrive(districtsArriveeData);
      setCommune(communesData);
      setCommunearriver(communesArriveeData);
      setActivitemis(activitesMisData);

      const initialForm = {
        type: rowType,
        description: row.activityDescription || '',
        dateDepart: row.dateDepart || '',
        dateArrivee: row.dateArrivee || '',
        heureDepart: row.heureDepart || '08:00',
        heureArrivee: row.heureArrivee || '18:00',
        region: row.activityDetails?.region || null,
        region_arriver: isRowTypeWithArrival ? (row.activityDetails?.region_arriver || null) : null,
        district: row.activityDetails?.district || null,
        district_arriver: isRowTypeWithArrival ? (row.activityDetails?.district_arriver || null) : null,
        commune: row.activityDetails?.commune || null,
        commune_arriver: isRowTypeWithArrival ? (row.activityDetails?.commune_arriver || null) : null,
        financement: row.activityDetails?.financement || [],
        // CORRECTION : on garantit un tableau propre
        activite_mis: normalizeActiviteMis(row.activityDetails?.activite_mis),
        activite_hors_mis: row.activityDetails?.activite_hors_mis || '',
      };

      setActivityForm(initialForm);
    } catch (error) {
      console.error("Erreur lors du chargement des données du dialogue:", error);
    } finally {
      if (isMountedRef.current) {
        setDialogLoading(false);
        setLoadingDistrict(false);
        setLoadingDistrictArrive(false);
        setLoadingCommune(false);
        setLoadingCommuneArrive(false);
        setLoadingActiviteMis(false);
      }
    }
  };

  const handleCloseActivityDialog = () => {
    setDialogOpen(false);
    setCurrentActivityRowId(null);
    setDialogLoading(false);
    setLoadingDistrict(false);
    setLoadingDistrictArrive(false);
    setLoadingCommune(false);
    setLoadingCommuneArrive(false);
    setLoadingActiviteMis(false);
    setActivityForm({
      type: '',
      description: '',
      dateDepart: '',
      dateArrivee: '',
      heureDepart: '08:00',
      heureArrivee: '18:00',
      region: null,
      region_arriver: null,
      district: null,
      district_arriver: null,
      commune: null,
      commune_arriver: null,
      financement: [],
      activite_mis: [],
      activite_hors_mis: '',
    });
    setDistrict([]);
    setCommune([]);
    setCommunearriver([]);
    setDistrictarrive([]);
    setActivitemis([]);
    setActvalide(null);
  };

  const handleSaveActivity = () => {
    if (!activityForm.dateDepart) {
      setMessage({ open: true, color: "error", messageInfo: "Veuillez renseigner la date de départ" });
      return;
    }

    if (!activityForm.dateArrivee) {
      setMessage({ open: true, color: "error", messageInfo: "Veuillez renseigner la date d'arrivée" });
      return;
    }

    if (!activityForm.heureDepart) {
      setMessage({ open: true, color: "error", messageInfo: "Veuillez renseigner l'heure de départ" });
      return;
    }

    if (!activityForm.heureArrivee) {
      setMessage({ open: true, color: "error", messageInfo: "Veuillez renseigner l'heure d'arrivée" });
      return;
    }

    const dateDepartObj = new Date(activityForm.dateDepart);
    const dateArriveeObj = new Date(activityForm.dateArrivee);

    if (dateArriveeObj < dateDepartObj) {
      setMessage({ open: true, color: "error", messageInfo: "La date d'arrivée ne peut pas être avant la date de départ" });
      return;
    }

    if (activityForm.dateDepart === activityForm.dateArrivee) {
      const [hDep, mDep] = activityForm.heureDepart.split(':').map(Number);
      const [hArr, mArr] = activityForm.heureArrivee.split(':').map(Number);
      const minutesDepart = hDep * 60 + mDep;
      const minutesArrivee = hArr * 60 + mArr;

      if (minutesArrivee < minutesDepart) {
        setMessage({
          open: true,
          color: "error",
          messageInfo: "L'heure d'arrivée ne peut pas être avant l'heure de départ pour la même journée",
        });
        return;
      }
    }

    if (!activityForm.commune || activityForm.commune.length <= 0) {
      setMessage({ open: true, color: "error", messageInfo: "Veuillez sélectionner une commune" });
      return;
    }

    if (activityForm.activite_hors_mis === "" && activityForm.type !== 'DR') {
      setMessage({
        open: true,
        color: "error",
        messageInfo: "Veuillez ajouter une activité effectuée par la mission",
      });
      return;
    }

    if (activityForm.type !== 'DR' && activityForm.financement.length <= 0) {
      setMessage({ open: true, color: "error", messageInfo: "Veuillez ajouter un financement" });
      return;
    }

    if (!activityForm.type) {
      setMessage({ open: true, color: "error", messageInfo: "Veuillez sélectionner un type d'activité" });
      return;
    }

    if (activityForm.type === 'DR') {
      if (!activityForm.region || !activityForm.region_arriver) {
        setMessage({
          open: true,
          color: "error",
          messageInfo: "Veuillez sélectionner les régions de départ et d'arrivée",
        });
        return;
      }
    }

    if (activityForm.type === 'mis') {
      if (!activityForm.activite_mis || activityForm.activite_mis.length === 0) {
        setMessage({
          open: true,
          color: "error",
          messageInfo: "Veuillez sélectionner au moins une activité MIS",
        });
        return;
      }
    }

    if (activityForm.type === 'hors_mis') {
      if (!activityForm.activite_hors_mis.trim()) {
        setMessage({
          open: true,
          color: "error",
          messageInfo: "Veuillez décrire l'activité hors MIS",
        });
        return;
      }
    }

    const isDR = activityForm.type === 'DR';

    const activityDetails = {
      region: activityForm.region,
      region_arriver: isDR ? activityForm.region_arriver : activityForm.region,
      district: activityForm.district,
      district_arriver: isDR ? activityForm.district_arriver : activityForm.district,
      commune: activityForm.commune,
      commune_arriver: isDR ? activityForm.commune_arriver : activityForm.commune,
      financement: activityForm.financement,
      activite_mis: normalizeActiviteMis(activityForm.activite_mis),
      activite_hors_mis: activityForm.activite_hors_mis,
    };

    setDateRanges(prevRanges =>
      prevRanges.map(range =>
        range.id === currentActivityRowId
          ? {
              ...range,
              dateDepart: activityForm.dateDepart,
              dateArrivee: activityForm.dateArrivee,
              heureDepart: activityForm.heureDepart,
              heureArrivee: activityForm.heureArrivee,
              activityType: activityForm.type,
              activityDescription: activityForm.description,
              activityDetails: activityDetails,
              isDuplicate: false,
            }
          : range
      )
    );

    handleCloseActivityDialog();
    setMessage({
      open: true,
      color: "success",
      messageInfo: "Activité ajoutée/modifiée avec succès",
    });
  };

  const handleChangetitle = (event) => {
    setIntutule(event.target.value);
  };

  const handleDateDebutChange = (event) => {
    setDateDebut(event.target.value);
  };

  const handleDateFinChange = (event) => {
    const finDate = event.target.value;
    setDateFin(finDate);

    if (dateDebut && finDate) {
      generateDateRanges(dateDebut, finDate);
    }
  };

  const generateDateRanges = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const dateArray = [];

    if (startDate > endDate) {
      setMessage({
        open: true,
        color: "error",
        messageInfo: "La date de fin doit être après la date de début",
      });
      setDateFin("");
      setDateRanges([]);
      return;
    }

    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      dateArray.push({
        id: Date.now() + Math.random(),
        dateDepart: dateStr,
        dateArrivee: dateStr,
        heureDepart: '08:00',
        heureArrivee: '18:00',
        activityType: '',
        activityDescription: '',
        activityDetails: {},
        isEditing: false,
        isDuplicate: false,
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }
    setPlaydate(false);
    setDateRanges(dateArray);
  };

  const handleDuplicateRow = (row) => {
    const newRow = {
      ...row,
      id: Date.now() + Math.random(),
      activityDetails: row.activityDetails ? { ...row.activityDetails } : {},
      isDuplicate: true,
    };
    setDateRanges(prev => [...prev, newRow]);
    setMessage({
      open: true,
      color: "warning",
      messageInfo: "Ligne dupliquée. Veuillez modifier les informations pour éviter un doublon.",
    });
  };

  const handleDelete = (id) => {
    setDateRanges(prevRanges => prevRanges.filter(range => range.id !== id));
    setMessage({
      open: true,
      color: "info",
      messageInfo: "Ligne supprimée",
    });
  };

  const handleSaveAndGoToPlanning = () => {
    if (intutule === "") {
      setMessage({
        open: true,
        color: "error",
        messageInfo: "Veuillez ajouter un intitulé",
      });
      return;
    } else if (dateRanges.length <= 0) {
      setMessage({
        open: true,
        color: "error",
        messageInfo: "Veuillez ajouter les détails dans l'agenda",
      });
      return;
    }

    if (hasDuplicates) {
      setMessage({
        open: true,
        color: "error",
        messageInfo: "Veuillez modifier les lignes dupliquées avant d'enregistrer",
      });
      return;
    }

    if (!canGoToPlanning) {
      setMessage({
        open: true,
        color: "error",
        messageInfo: "Veuillez sélectionner un planning pour pouvoir y accéder",
      });
      return;
    }

    setLoading(true);

    const handleSuccess = () => {
      setMessage({ ...message, open: true, color: 'green', messageInfo: "Succès - Redirection vers le planning..." });
      setLoading(false);
      setTimeout(function () {
        navigate("/mission/planning_mission/validate/add/" + planningid);
      }, 2000);
    };

    const handleError = () => {
      setMessage({ ...message, open: true, color: 'red', messageInfo: 'Une erreur s\'est produite lors de l\'insertion, veuillez réessayer' });
      setLoading(false);
    };

    if (idagenda !== undefined) {
      MissionService.updateagendanew(intutule, dateRanges, idagenda).then((result) => {
        if (!isMountedRef.current) return;
        if (result) {
          handleSuccess();
        } else {
          handleError();
        }
      });
    } else {
      MissionService.insertagendanew(intutule, dateRanges, planningid, destinationid).then((result) => {
        if (!isMountedRef.current) return;
        if (result) {
          handleSuccess();
        } else {
          handleError();
        }
      });
    }
  };

  const handleSaveFinale = () => {
    if (intutule === "") {
      setMessage({
        open: true,
        color: "error",
        messageInfo: "Veuillez ajouter un intitulé",
      });
      return;
    } else if (dateRanges.length <= 0) {
      setMessage({
        open: true,
        color: "error",
        messageInfo: "Veuillez ajouter les détails dans l'agenda",
      });
      return;
    }

    if (hasDuplicates) {
      setMessage({
        open: true,
        color: "error",
        messageInfo: "Veuillez modifier les lignes dupliquées avant d'enregistrer",
      });
      return;
    }

    else {
      setLoading(true);
      if (idagenda !== undefined) {
        MissionService.updateagendanew(intutule, dateRanges, idagenda).then((result) => {
          if (!isMountedRef.current) return;

          if (result) {
            setMessage({ ...message, open: true, color: 'green', messageInfo: "Succès" });
            setLoading(false);
            setTimeout(function () { navigate("/mission/agendalist"); }, 2000);
          }
          else {
            setMessage({ ...message, open: true, color: 'red', messageInfo: 'Une erreur s\'est produite lors de l\'insertion, veuillez réessayer' });
            setLoading(false);
          }
        })
      } else {
        MissionService.insertagendanew(intutule, dateRanges, planningid, destinationid).then((result) => {
          if (!isMountedRef.current) return;

          if (result) {
            setMessage({ ...message, open: true, color: 'green', messageInfo: "Succès" });
            setLoading(false);
            setTimeout(function () { navigate("/mission/agendalist"); }, 2000);
          }
          else {
            setMessage({ ...message, open: true, color: 'red', messageInfo: 'Une erreur s\'est produite lors de l\'insertion, veuillez réessayer' });
            setLoading(false);
          }
        });
      }
    }
  }

  const handleactivitetype = (field, value) => {
    if (!value) {
      setActvalide(null);
      setActivitemis([]);
      setLoadingActiviteMis(false);
      return;
    }

    setActvalide(value);
    loadActivitesMis(activityForm.financement, activityForm.commune);
  }

  // ============================================================
  // CORRECTION MAJEURE : handleActivityFormChange
  // - On gère le cas "activite_mis" de manière ATOMIQUE
  // - On évite tout rechargement des options lors de la sélection MIS
  // ============================================================
  const handleActivityFormChange = (field, value) => {
    // CAS SPÉCIAL : sélection multiple d'activités MIS
    // On met à jour UNIQUEMENT activite_mis, sans toucher aux options
    if (field === "activite_mis") {
      setActivityForm(prev => ({
        ...prev,
        activite_mis: normalizeActiviteMis(value),
      }));
      return;
    }

    // Mise à jour standard
    setActivityForm(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === "type") {
      const newType = value;
      const newTypeHasArrival = newType === 'DR';

      if (!newTypeHasArrival) {
        setDistrictarrive([]);
        setCommunearriver([]);
        setActivityForm(prev => ({
          ...prev,
          region_arriver: null,
          district_arriver: null,
          commune_arriver: null,
        }));
      }

      if (newType !== 'mis' && newType !== 'hors_mis') {
        setActivityForm(prev => ({
          ...prev,
          financement: [],
          activite_mis: [],
          activite_hors_mis: '',
        }));
        setActivitemis([]);
        setActvalide(null);
        setLoadingActiviteMis(false);
      }

      if (newType === 'mis') {
        setActivityForm(prev => ({
          ...prev,
          financement: (prev.financement || []).filter(
            (f) => f.state && f.state.toLowerCase() === 'en cours'
          ),
          activite_mis: [],
        }));
        setActivitemis([]);
        setActvalide(null);
        setLoadingActiviteMis(false);
      }

      return;
    }

    if (field === "region") {
      setDistrict([]);
      setCommune([]);
      setActivityForm(prev => ({ ...prev, district: null, commune: null }));

      if (value?.id_region) {
        setLoadingDistrict(true);
        LocalisationMisService.getDistrictByRegion(value.id_region)
          .then((result) => {
            if (!isMountedRef.current) return;

            if (result?.data) {
              setDistrict(result.data);
            }
          })
          .catch(error => {
            console.error("Erreur lors de la récupération des districts:", error);
          })
          .finally(() => {
            if (isMountedRef.current) {
              setLoadingDistrict(false);
            }
          });
      }
    }
    else if (field === "region_arriver") {
      setDistrictarrive([]);
      setCommunearriver([]);
      setActivityForm(prev => ({ ...prev, district_arriver: null, commune_arriver: null }));

      if (value?.id_region) {
        setLoadingDistrictArrive(true);
        LocalisationMisService.getDistrictByRegion(value.id_region)
          .then((result) => {
            if (!isMountedRef.current) return;

            if (result?.data) {
              setDistrictarrive(result.data);
            }
          })
          .catch(error => {
            console.error("Erreur lors de la récupération des districts (arrivée):", error);
          })
          .finally(() => {
            if (isMountedRef.current) {
              setLoadingDistrictArrive(false);
            }
          });
      }
    }
    else if (field === "district") {
      setCommune([]);
      setActivityForm(prev => ({ ...prev, commune: null }));

      if (value?.id_district) {
        setLoadingCommune(true);
        LocalisationMisService.getCommunesByDistrict(value.id_district)
          .then((result) => {
            if (!isMountedRef.current) return;

            if (result && result.data) {
              setCommune(result.data);
            }
          })
          .finally(() => {
            if (isMountedRef.current) {
              setLoadingCommune(false);
            }
          });
      }
    }
    else if (field === "district_arriver") {
      setCommunearriver([]);
      setActivityForm(prev => ({ ...prev, commune_arriver: null }));

      if (value?.id_district) {
        setLoadingCommuneArrive(true);
        LocalisationMisService.getCommunesByDistrict(value.id_district)
          .then((result) => {
            if (!isMountedRef.current) return;

            if (result && result.data) {
              setCommunearriver(result.data);
            }
          })
          .finally(() => {
            if (isMountedRef.current) {
              setLoadingCommuneArrive(false);
            }
          });
      }
    }
    else if (field === "financement") {
      if (activityForm.type === 'mis') {
        // Réinitialise les activités MIS sélectionnées car les financements ont changé
        setActivityForm(prev => ({
          ...prev,
          financement: value,
          activite_mis: [],
        }));
        setActivitemis([]);
        setActvalide(null);

        // Recharge les options d'activités MIS disponibles
        loadActivitesMis(value, activityForm.commune);
      }
      // Pour 'hors_mis', on ne touche pas à activite_mis ni aux options
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getActivityLabel = (value) => {
    const activity = ACTIVITY_TYPES.find(a => a.value === value);
    return activity ? activity.label : 'Non définie';
  };

  const getActivityDetails = (range) => {
    if (!range.activityType) return '';

    const details = [];

    if (range.activityDetails?.commune?.nom_commune) {
      const communeDepart = range.activityDetails.commune.nom_commune || '';
      const communeArrivee = range.activityDetails.commune_arriver?.nom_commune || '';
      if (communeArrivee && communeArrivee !== communeDepart) {
        details.push(`${communeDepart} → ${communeArrivee}`);
      } else {
        details.push(communeDepart);
      }
    }

    if (range.activityType === 'mis' && range.activityDetails?.activite_mis?.length > 0) {
      details.push(`MIS: ${range.activityDetails.activite_mis.map(a => a.libelle_type_activite || a.libelle_fr).join(', ')}`);
    }

    if (range.activityType === 'hors_mis' && range.activityDetails?.activite_hors_mis) {
      details.push(`Hors MIS: ${range.activityDetails.activite_hors_mis.substring(0, 50)}${range.activityDetails.activite_hors_mis.length > 50 ? '...' : ''}`);
    }

    if (range.activityDetails?.financement?.length > 0) {
      details.push(`Financement: ${range.activityDetails.financement.map(f => f.code_financement).join(', ')}`);
    }

    return details.join('\n');
  };

  function handleCloseMessage() {
    setMessage({ ...message, open: false });
  }

  const sortDateRanges = (ranges) => {
    return [...ranges].sort((a, b) => {
      const dateDiff = new Date(a.dateDepart) - new Date(b.dateDepart);
      if (dateDiff !== 0) return dateDiff;

      const heureDepartA = parseInt(a.heureDepart.replace(':', ''));
      const heureDepartB = parseInt(b.heureDepart.replace(':', ''));
      return heureDepartA - heureDepartB;
    });
  };

  const allActivitiesDefined = dateRanges.every(range => range.activityType);

  const handlePlanningChange = (event, newValue) => {
    setPlanningid(newValue?.mission_planning_id ?? null);

    MissionService.detaildestination(newValue?.mission_planning_id ?? null).then((result) => {
      if (!isMountedRef.current) return;

      if (result && result.data) {
        setListeDestination(result.data);

        if (result.data.length === 1) {
          const uniqueDestination = result.data[0];
          setDestinationid(uniqueDestination.planning_destination ?? null);

          const dateBegin = uniqueDestination?.date_debut;
          const dateFin = uniqueDestination?.date_fin;

          if (dateBegin && dateFin) {
            generateDateRanges(dateBegin, dateFin);
            setPlaydate(false);
          }

          setMessage({
            open: true,
            color: "info",
            messageInfo: "Destination unique sélectionnée automatiquement",
          });
        } else if (result.data.length > 1) {
          setDestinationid(0);
          setDateRanges([]);
          setPlaydate(true);

          setMessage({
            open: true,
            color: "warning",
            messageInfo: "Veuillez choisir une destination pour l'agenda",
          });
        }
      } else {
        setListeDestination([]);
        setDestinationid(0);
        setDateRanges([]);
      }
    });
  };

  const handleDestinationChange = (event, newValue) => {
    if (newValue.status === "Agenda existe") {
      setOpenLoadingSimpleBackdrop(true);
      MissionService.detailagendanew(newValue.mission_planning_agenda, newValue.planning_destination).then((result) => {
        if (!isMountedRef.current) return;

        if (result && result.data) {
          MissionService.detaildestination(result.planningid)
            .then((results) => {
              if (!isMountedRef.current) return;

              if (results && results.data) {
                const details = results.data;
                setListeDestination(details);
                setDateRanges(result.data);
                setIntutule(result.title);
                setPlanningid(result.planningid);
                setDestinationid(newValue.planning_destination);
                setPlaydate(false);
                setOpenLoadingSimpleBackdrop(false);
              }
            })
            .catch((error) => {
              if (isMountedRef.current) {
                setListeDestination([]);
                setOpenLoadingSimpleBackdrop(false);
              }
            });
        }
        else {
          if (isMountedRef.current) {
            setOpenLoadingSimpleBackdrop(false);
          }
        }
      });
    } else {
      setDestinationid(newValue?.planning_destination ?? null);
      const dateBegin = newValue?.date_debut;
      const dateFin = newValue?.date_fin;

      if (dateBegin && dateFin) {
        generateDateRanges(dateBegin, dateFin);
        setPlaydate(false);
      }
    }
  };

  useEffect(() => {
    let isCancelled = false;
    setOpenLoadingSimpleBackdrop(true);
    setInitialDataLoading(true);

    Promise.all([
      LocalisationMisService.getRegions(),
      LocalisationMisService.getFinancement(),
      LocalisationMisService.gettypeactivite(),
      MissionService.detailplanning()
    ])
      .then(([regionsResult, financementResult, typeactiviteResult, planningResult]) => {
        if (isCancelled || !isMountedRef.current) return;

        if (regionsResult && regionsResult.data) {
          setRegion(regionsResult.data);
        }

        if (financementResult) {
          setFinancement(financementResult);
        }

        if (typeactiviteResult) {
          setTypeactivite(typeactiviteResult.data);
        }

        if (planningResult && planningResult.data) {
          setListeplanning(planningResult.data);
        } else {
          setListeplanning([]);
        }

        setInitialDataLoading(false);
        setOpenLoadingSimpleBackdrop(false);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des données:", error);
        if (!isCancelled && isMountedRef.current) {
          setListeplanning([]);
          setInitialDataLoading(false);
          setOpenLoadingSimpleBackdrop(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (idagenda !== undefined) {
      let isCancelled = false;
      setOpenLoadingSimpleBackdrop(true);
      setEditDataLoading(true);
      setPlaydate(false);

      MissionService.detailagendanew(idagenda, iddestination)
        .then((result) => {
          if (isCancelled || !isMountedRef.current) return;

          if (result && result.data) {

            return MissionService.detaildestination(result.planningid)
              .then((results) => {
                if (isCancelled || !isMountedRef.current) return;

                if (results && results.data) {
                  setListeDestination(results.data);

                } else {
                  setListeDestination([]);
                }

                if (result.data.length > 0) {
                  setDateRanges(result.data);
                }
                else {
                  setDateDebut(result.datedebut);
                  setDateFin(result.datefin);
                  generateDateRanges(result.datedebut, result.datefin);
                }

                setIntutule(result.title);
                setPlanningid(result.planningid);
                setDestinationid(result.destinationid);
              });
          } else {
            throw new Error("Aucune donnée reçue");
          }
        })
        .catch((error) => {
          console.error("Erreur dans le chargement en modification:", error);
          if (!isCancelled && isMountedRef.current) {
            setListeDestination([]);
          }
        })
        .finally(() => {
          if (!isCancelled && isMountedRef.current) {
            setEditDataLoading(false);
            setOpenLoadingSimpleBackdrop(false);
            setPlaydate(false);

          }
        });

      return () => {
        isCancelled = true;
      };
    }
  }, [idagenda, iddestination]);

  useEffect(() => {
    const isLoading = initialDataLoading || editDataLoading;
    setOpenLoadingSimpleBackdrop(isLoading);
  }, [initialDataLoading, editDataLoading]);

  const getPlanningLabel = (option) => {
    if (!option) return "";
    const title = option.mission_title || "";
    const reference = option.reference || "";
    if (reference) {
      return `${title} - ${reference}`;
    }
    return title;
  };

  return (
    <MainContainer {...headerDataMission}>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />
      <Message open={open} color={color} messageInfo={messageInfo} handleClose={handleCloseMessage} />
      <Paper className={classes.paper}>
        <Grid container spacing={3}>
          <Grid item xs={2}>
            <label className={classes.labelPosition}>Planning mission :</label>
          </Grid>
          <Grid item xs={10}>
            <Autocomplete
              value={listeplanning.find((p) => p.mission_planning_id === planningid) || null}
              onChange={handlePlanningChange}
              options={listeplanning}
              isOptionEqualToValue={(option, value) =>
                Number(option.mission_planning_id) === Number(value.mission_planning_id)
              }
              getOptionLabel={getPlanningLabel}
              renderOption={(props, option) => (
                <li {...props} key={option.mission_planning_id}>
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {option.mission_title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Référence : {option.reference || "N/A"}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Planning mission"
                  size="small"
                  variant="outlined"
                  required
                />
              )}
            />
          </Grid>
          <Grid item xs={2}>
            <label className={classes.labelPosition}>Destination de la mission:</label>
          </Grid>
          <Grid item xs={10}>
            <Autocomplete
              value={listedestination.find(p => p.planning_destination === destinationid) || null}
              onChange={handleDestinationChange}
              options={listedestination}
              getOptionLabel={(option) =>
                option ? `${option.lieufinal} (${option.status})` : ""
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Destination de la mission"
                  size="small"
                  variant="outlined"
                  required
                  error={listedestination.length > 1 && !destinationid}
                  helperText={listedestination.length > 1 && !destinationid ? "Veuillez choisir une destination" : ""}
                />
              )}
              disabled={listedestination.length === 0}
            />
            {listedestination.length > 1 && !destinationid && (
              <Typography variant="caption" color="error" style={{ marginLeft: '14px', marginTop: '4px' }}>
                ⚠ Sélectionnez une destination pour continuer
              </Typography>
            )}
          </Grid>
          <Grid item xs={2}>
            <label className={classes.labelPosition}>Intitulé :</label>
          </Grid>
          <Grid item xs={10}>
            <TextField
              fullWidth
              id="intitule"
              size="small"
              label="Intitulé"
              variant="outlined"
              value={intutule}
              name="intutule"
              onChange={handleChangetitle}
              placeholder={getPlaceholderTitle()}
              required
            />
          </Grid>

          {playdate &&
            <>
              <Grid item xs={2}>
                <label className={classes.labelPosition}>Date :</label>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  id="date-debut"
                  size="small"
                  type="date"
                  label="Date de début"
                  variant="outlined"
                  value={dateDebut}
                  name="dateDebut"
                  onChange={handleDateDebutChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={2}>
                <label className={classes.labelPosition}>Date fin :</label>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  id="date-fin"
                  size="small"
                  type="date"
                  label="Date de fin"
                  variant="outlined"
                  value={dateFin}
                  name="dateFin"
                  onChange={handleDateFinChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </>}
        </Grid>
      </Paper>

      <Box className={classes.addButtonContainer}>
        <Box className={classes.infoText}>
          <Tooltip title="Cliquez sur une ligne pour afficher/modifier les détails de l'activité">
            <InfoIcon fontSize="small" style={{ marginRight: 8 }} />
          </Tooltip>
          {dateRanges.length > 0 && (
            <span>
              {dateRanges.length} ligne(s) -
              <span style={{
                color: allActivitiesDefined ? 'green' : 'orange',
                fontWeight: 'bold',
                marginLeft: '8px'
              }}>
                {allActivitiesDefined ? '✓ Toutes les activités sont définies' : '⚠ Certaines activités manquent'}
              </span>
              {hasDuplicates && (
                <span style={{ color: '#e65100', fontWeight: 'bold', marginLeft: '12px' }}>
                  ⚠ {duplicateRowIds.size} ligne(s) en doublon à modifier
                </span>
              )}
            </span>
          )}
        </Box>
      </Box>

      {(dateRanges.length > 0 || isAddingNew) && (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Typography variant="h6" gutterBottom style={{ padding: '16px' }}>
            Planification des dates
            {!allActivitiesDefined && (
              <Alert severity="error" style={{ marginLeft: '16px' }}>
                Veuillez définir une activité pour chaque ligne
              </Alert>
            )}
            {hasDuplicates && (
              <Alert severity="warning" style={{ marginLeft: '16px' }}>
                Certaines lignes sont identiques (doublons). Veuillez les modifier avant d'enregistrer.
              </Alert>
            )}
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>Date de départ</TableCell>
                <TableCell className={classes.tableHeader}>Date d'arrivée</TableCell>
                <TableCell className={classes.tableHeader}>Heure de départ</TableCell>
                <TableCell className={classes.tableHeader}>Heure d'arrivée</TableCell>
                <TableCell className={classes.tableHeader}>Activité</TableCell>
                <TableCell className={classes.tableHeader}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortDateRanges(dateRanges).map((range) => {
                const hasActivity = !!range.activityType;
                const activityDetails = getActivityDetails(range);
                const isDuplicateRow = duplicateRowIds.has(range.id);

                return (
                  <TableRow
                    key={range.id}
                    className={`${classes.clickableRow} ${isDuplicateRow ? classes.duplicateDateRow : ''} ${isDuplicateRow ? classes.duplicateRowHighlight : ''}`}
                    onClick={() => handleOpenActivityDialog(range.id)}
                    hover
                  >
                    <TableCell>
                      <div>{formatDate(range.dateDepart)}</div>
                    </TableCell>
                    <TableCell>
                      <div>{formatDate(range.dateArrivee)}</div>
                    </TableCell>
                    <TableCell>{range.heureDepart}</TableCell>
                    <TableCell>{range.heureArrivee}</TableCell>
                    <TableCell className={classes.activityCell}>
                      <Box>
                        <Typography
                          variant="body2"
                          style={{
                            color: hasActivity ? 'inherit' : 'red',
                            fontWeight: hasActivity ? 'normal' : 'bold'
                          }}
                        >
                          {hasActivity ? getActivityLabel(range.activityType) : '⚠ Aucune activité'}
                        </Typography>
                        {activityDetails && (
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '4px', whiteSpace: 'pre-line' }}>
                            {activityDetails}
                          </Typography>
                        )}
                        {range.activityDescription && (
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '4px' }}>
                            {range.activityDescription}
                          </Typography>
                        )}
                        {!hasActivity && (
                          <Typography className={classes.errorText}>
                            Activité obligatoire
                          </Typography>
                        )}
                        {isDuplicateRow && (
                          <Typography className={classes.duplicateAlert}>
                            ⚠ Doublon détecté : veuillez modifier cette ligne
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        color={isDuplicateRow ? 'secondary' : 'primary'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenActivityDialog(range.id);
                        }}
                        className={classes.activityButton}
                        style={{ marginTop: '8px' }}
                      >
                        {isDuplicateRow
                          ? 'Modifier pour éviter le doublon'
                          : hasActivity
                            ? 'Modifier activité/date'
                            : 'Ajouter activité/date'}
                      </Button>
                    </TableCell>
                    <TableCell className={classes.actionCell}>
                      <Tooltip title="Dupliquer">
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateRow(range);
                          }}
                          size="small"
                        >
                          <DuplicateIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          color="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(range.id);
                          }}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Grid container spacing={3} justifyContent="flex-end" style={{ marginTop: '16px' }}>
        <Grid item>
          <LoadingButton
            onClick={handleSaveFinale}
            color="primary"
            variant="contained"
            loading={isloading}
            loadingPosition="center"
            disabled={!allActivitiesDefined || isloading || hasDuplicates}
          >
            Enregistrer
          </LoadingButton>
        </Grid>

        {canGoToPlanning && (
          <Grid item>
            <LoadingButton
              onClick={handleSaveAndGoToPlanning}
              color="secondary"
              variant="contained"
              loading={isloading}
              loadingPosition="center"
              disabled={!allActivitiesDefined || isloading || hasDuplicates}
            >
              Enregistrer et passer au planning
            </LoadingButton>
          </Grid>
        )}
      </Grid>

      <BootstrapDialog
        onClose={handleCloseActivityDialog}
        aria-labelledby="activity-dialog-title"
        open={dialogOpen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="activity-dialog-title">
          {currentActivityRowId ? "Modifier l'activité" : "Ajouter une activité"}
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          {dialogLoading && (
            <Box className={classes.dialogLoadingOverlay}>
              <CircularProgress size={48} />
              <Typography className={classes.loadingText}>
                Chargement des données en cours...
              </Typography>
            </Box>
          )}

          <Grid
            container
            spacing={2}
            className={classes.formRow}
            style={{ visibility: dialogLoading ? 'hidden' : 'visible' }}
          >
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Date de départ *"
                value={activityForm.dateDepart}
                onChange={(e) => handleActivityFormChange('dateDepart', e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Date d'arrivée *"
                value={activityForm.dateArrivee}
                onChange={(e) => handleActivityFormChange('dateArrivee', e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="time"
                label="Heure de départ *"
                value={activityForm.heureDepart}
                onChange={(e) => handleActivityFormChange('heureDepart', e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="time"
                label="Heure d'arrivée *"
                value={activityForm.heureArrivee}
                onChange={(e) => handleActivityFormChange('heureArrivee', e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Type d'activité *</InputLabel>
                <Select
                  value={activityForm.type}
                  onChange={(e) => handleActivityFormChange('type', e.target.value)}
                  label="Type d'activité *"
                >
                  <MenuItem value=""><em>Sélectionner</em></MenuItem>
                  {ACTIVITY_TYPES.map((activity) => (
                    <MenuItem key={activity.value} value={activity.value}>
                      {activity.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                {isTypeWithArrival ? 'Lieux (départ → arrivée)' : 'Lieu de la mission'}
              </Typography>
            </Grid>

            <Grid item xs={isTypeWithArrival ? 6 : 12}>
              <Autocomplete
                value={activityForm.region}
                onChange={(event, newValue) => handleActivityFormChange('region', newValue)}
                options={region}
                getOptionLabel={(option) => option.nom_region || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={isTypeWithArrival ? "Région départ" : "Région"}
                    size="small"
                    variant="outlined"
                    required
                  />
                )}
              />
            </Grid>

            {isTypeWithArrival && (
              <Grid item xs={6}>
                <Autocomplete
                  value={activityForm.region_arriver}
                  onChange={(event, newValue) => handleActivityFormChange('region_arriver', newValue)}
                  options={region}
                  getOptionLabel={(option) => option.nom_region || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Région arrivée"
                      size="small"
                      variant="outlined"
                      required
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={isTypeWithArrival ? 6 : 12}>
              {loadingDistrict ? (
                <Box className={classes.fieldLoadingContainer}>
                  <CircularProgress size={20} />
                  <Typography className={classes.fieldLoadingText}>
                    Chargement des districts...
                  </Typography>
                </Box>
              ) : (
                district.length > 0 && (
                  <Autocomplete
                    value={activityForm.district}
                    onChange={(event, newValue) => handleActivityFormChange('district', newValue)}
                    options={district}
                    getOptionLabel={(option) => option.nom_district || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label={isTypeWithArrival ? "District départ" : "District"}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                )
              )}
            </Grid>

            {isTypeWithArrival && (
              <Grid item xs={6}>
                {loadingDistrictArrive ? (
                  <Box className={classes.fieldLoadingContainer}>
                    <CircularProgress size={20} />
                    <Typography className={classes.fieldLoadingText}>
                      Chargement des districts...
                    </Typography>
                  </Box>
                ) : (
                  districtarrive.length > 0 && (
                    <Autocomplete
                      value={activityForm.district_arriver}
                      onChange={(event, newValue) => handleActivityFormChange('district_arriver', newValue)}
                      options={districtarrive}
                      getOptionLabel={(option) => option.nom_district || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="District arrivée"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    />
                  )
                )}
              </Grid>
            )}

            <Grid item xs={isTypeWithArrival ? 6 : 12}>
              {loadingCommune ? (
                <Box className={classes.fieldLoadingContainer}>
                  <CircularProgress size={20} />
                  <Typography className={classes.fieldLoadingText}>
                    Chargement des communes...
                  </Typography>
                </Box>
              ) : (
                commune.length > 0 && (
                  <Autocomplete
                    value={activityForm.commune}
                    onChange={(event, newValue) => handleActivityFormChange('commune', newValue)}
                    options={commune}
                    getOptionLabel={(option) => option.nom_commune || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label={isTypeWithArrival ? "Commune départ" : "Commune"}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                )
              )}
            </Grid>

            {isTypeWithArrival && (
              <Grid item xs={6}>
                {loadingCommuneArrive ? (
                  <Box className={classes.fieldLoadingContainer}>
                    <CircularProgress size={20} />
                    <Typography className={classes.fieldLoadingText}>
                      Chargement des communes...
                    </Typography>
                  </Box>
                ) : (
                  communearriver.length > 0 && (
                    <Autocomplete
                      value={activityForm.commune_arriver}
                      onChange={(event, newValue) => handleActivityFormChange('commune_arriver', newValue)}
                      options={communearriver}
                      getOptionLabel={(option) => option.nom_commune || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Commune arrivée"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    />
                  )
                )}
              </Grid>
            )}

            {(activityForm.type === 'mis' || activityForm.type === 'hors_mis') && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  value={activityForm.financement}
                  onChange={(event, value) => handleActivityFormChange('financement', value)}
                  options={financementOptions}
                  getOptionLabel={(option) => option.code_financement || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option.id_financement) === String(value.id_financement)
                  }
                  filterOptions={(options, { inputValue }) => {
                    return options.filter((option) =>
                      option.code_financement?.toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Financement *"
                      size="small"
                      variant="outlined"
                      placeholder="Recherche ici"
                    />
                  )}
                />
              </Grid>
            )}

            {activityForm.type === 'mis' && (
              <>
                <Grid item xs={12}>
                  {loadingActiviteMis ? (
                    <Box className={classes.fieldLoadingContainer}>
                      <CircularProgress size={20} />
                      <Typography className={classes.fieldLoadingText}>
                        Chargement des activités MIS...
                      </Typography>
                    </Box>
                  ) : (
                    <Autocomplete
                      multiple
                      // CORRECTION : disableCloseOnSelect permet de sélectionner plusieurs
                      // éléments sans fermer la liste déroulante
                      disableCloseOnSelect
                      // CORRECTION : on garde la valeur courante bien normalisée
                      value={normalizeActiviteMis(activityForm.activite_mis)}
                      onChange={(event, value) =>
                        handleActivityFormChange('activite_mis', value)
                      }
                      options={activitemis}
                      getOptionLabel={(option) =>
                        option.libelle_type_activite || option.libelle_fr || ""
                      }
                      // CORRECTION : comparaison robuste sur plusieurs clés possibles
                      isOptionEqualToValue={(option, value) =>
                        getActiviteMisKey(option) === getActiviteMisKey(value)
                      }
                      filterOptions={(options, { inputValue }) => {
                        const search = (inputValue || '').toLowerCase();
                        return options.filter((option) =>
                          (option.libelle_type_activite || option.libelle_fr || '')
                            .toLowerCase()
                            .includes(search)
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Activité MIS *"
                          size="small"
                          variant="outlined"
                          placeholder="Recherche ici"
                          required
                        />
                      )}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Activités effectuées au cours de la mission *"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={activityForm.activite_hors_mis}
                    onChange={(e) => handleActivityFormChange('activite_hors_mis', e.target.value)}
                    required
                  />
                </Grid>
              </>
            )}

            {activityForm.type === 'hors_mis' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Activités effectuées au cours de la mission *"
                  variant="outlined"
                  multiline
                  rows={3}
                  value={activityForm.activite_hors_mis}
                  onChange={(e) => handleActivityFormChange('activite_hors_mis', e.target.value)}
                  required
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActivityDialog} color="secondary">
            Annuler
          </Button>
          <Button
            onClick={handleSaveActivity}
            color="primary"
            variant="contained"
            disabled={!activityForm.type || dialogLoading || loadingActiviteMis}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </MainContainer>
  );
}

export default AgendaMission;